import { useCallback, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  checkRegisterNickname,
  inferNicknameAvailable,
  registerLeadWelcome,
} from "@/services/leadService";

const SILK_BG =
  "https://images.unsplash.com/photo-1707573090905-8465126a393e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwcmVkJTIwYWJzdHJhY3QlMjBzaWxrJTIwdGV4dHVyZXxlbnwwfHx8fDE3Nzc1NDMyNjF8MA&ixlib=rb-4.1.0&q=85";

/**
 * Tras el último paso del onboarding: nickname + contraseña.
 * Comprueba nickname en blur (POST `/lead/register/check-nickname`).
 * Registro final: POST `/lead/register` con `id`, `nickname`, `password`, `password_confirmation`.
 *
 * @param {{
 *   leadRequestId: string;
 *   onRegistered: (data?: { nickname: string }) => void;
 *   onBack: () => void;
 * }} props
 */
export default function OnboardingRegisterPanel({ leadRequestId, onRegistered, onBack }) {
  const [nickname, setNickname] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [formError, setFormError] = useState("");
  const [nickHint, setNickHint] = useState({ status: "idle", message: "" });
  const checkAbortRef = useRef(null);
  const lastCheckedNick = useRef("");

  const registerMutation = useMutation({
    mutationFn: () =>
      registerLeadWelcome({
        id: leadRequestId,
        nickname: nickname.trim(),
        password,
      }),
    onSuccess: () => {
      toast.success("Acceso creado.");
      onRegistered?.({ nickname: nickname.trim() });
    },
    onError: (err) => {
      toast.error(err?.message || "No se pudo completar el registro");
    },
  });

  const runNicknameCheck = useCallback(async () => {
    const n = nickname.trim();
    if (n.length < 2) {
      setNickHint({ status: "idle", message: "" });
      lastCheckedNick.current = "";
      return;
    }

    checkAbortRef.current?.abort();
    const ac = new AbortController();
    checkAbortRef.current = ac;

    setNickHint({ status: "checking", message: "Comprobando disponibilidad…" });
    try {
      const data = await checkRegisterNickname(n, { signal: ac.signal });
      if (ac.signal.aborted) return;

      const avail = inferNicknameAvailable(data);
      const serverMsg =
        typeof data?.message === "string" && data.message.trim() ? data.message.trim() : "";

      if (avail === true) {
        setNickHint({
          status: "available",
          message: serverMsg || "Este nickname está libre. Puedes usarlo.",
        });
        lastCheckedNick.current = n;
      } else if (avail === false) {
        setNickHint({
          status: "taken",
          message: serverMsg || "Este nickname ya está en uso. Prueba con otro.",
        });
        lastCheckedNick.current = n;
      } else {
        setNickHint({
          status: "available",
          message: serverMsg || "Disponible.",
        });
        lastCheckedNick.current = n;
      }
    } catch (err) {
      if (
        (typeof axios.isCancel === "function" && axios.isCancel(err)) ||
        err?.code === "ERR_CANCELED" ||
        err?.name === "CanceledError" ||
        err?.name === "AbortError"
      ) {
        return;
      }
      setNickHint({
        status: "error",
        message: err?.message || "No se pudo comprobar el nickname.",
      });
      lastCheckedNick.current = "";
    }
  }, [nickname]);

  function onNicknameBlur() {
    const n = nickname.trim();
    if (n.length >= 2 && n === lastCheckedNick.current && nickHint.status === "available") {
      return;
    }
    runNicknameCheck();
  }

  function onNicknameChange(e) {
    const v = e.target.value;
    setNickname(v);
    if (nickHint.status !== "checking") {
      setNickHint({ status: "idle", message: "" });
    }
    setFormError("");
  }

  function handleSubmit(e) {
    e.preventDefault();
    setFormError("");
    const n = nickname.trim();
    if (!leadRequestId) {
      setFormError("Falta el identificador del enlace. Vuelve a abrir el correo de bienvenida.");
      return;
    }
    if (n.length < 2 || n.length > 32) {
      setFormError("El nickname debe tener entre 2 y 32 caracteres.");
      return;
    }
    if (nickHint.status !== "available") {
      setFormError("Comprueba el nickname (sal del campo para validarlo) hasta ver el mensaje en verde.");
      return;
    }
    if (password.length < 8) {
      setFormError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== password2) {
      setFormError("Las contraseñas no coinciden.");
      return;
    }
    registerMutation.mutate();
  }

  const nickFieldRing =
    nickHint.status === "available"
      ? "ring-1 ring-emerald-500/55 shadow-[0_0_0_1px_rgba(16,185,129,0.25)]"
      : nickHint.status === "taken"
        ? "ring-1 ring-red-500/55 shadow-[0_0_0_1px_rgba(239,68,68,0.25)]"
        : nickHint.status === "error"
          ? "ring-1 ring-amber-500/50 shadow-[0_0_0_1px_rgba(245,158,11,0.2)]"
          : nickHint.status === "checking"
            ? "ring-1 ring-white/15"
            : "ring-1 ring-transparent";

  const hintColor =
    nickHint.status === "available"
      ? "text-emerald-300/95"
      : nickHint.status === "taken"
        ? "text-red-300/95"
        : nickHint.status === "error"
          ? "text-amber-200/95"
          : "text-[rgba(245,245,245,0.5)]";

  const canSubmit =
    nickHint.status === "available" &&
    nickname.trim().length >= 2 &&
    password.length >= 8 &&
    password === password2 &&
    Boolean(leadRequestId) &&
    !registerMutation.isPending;

  return (
    <div className="relative h-[100dvh] max-h-[100dvh] w-full overflow-hidden bg-[#0a0a0a] text-[#f5f5f5]">
      <div
        className="pointer-events-none absolute inset-0 z-[1] opacity-[0.22]"
        style={{
          backgroundImage: `url(${SILK_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(2px) saturate(85%)",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 z-[2]"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(139,30,63,0.14), transparent 58%), linear-gradient(180deg, rgba(10,10,10,0.45), rgba(10,10,10,0.92))",
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 z-[3] mix-blend-overlay opacity-25"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.35'/></svg>\")",
        }}
        aria-hidden
      />

      <div className="relative z-10 flex h-full min-h-0 items-center justify-center overflow-y-auto px-4 py-8 md:px-8">
        <div className="w-full max-w-md py-4">
          <div
            className="zx-card zx-card-static zx-glass rounded-2xl border border-[rgba(255,255,255,0.1)] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.55)] md:p-8"
            data-testid="onboarding-register-panel"
          >
            <header className="mb-8 space-y-2 text-center">
              <p className="zx-label">Último paso</p>
              <h1 className={["font-serif text-2xl text-[#f5f5f5] md:text-[1.65rem]"].join(" ")}>
                Tu acceso a La Lista
              </h1>
              <p className="text-sm text-[rgba(245,245,245,0.6)]">
                Elige un nickname y una contraseña. No uses datos que te identifiquen.
              </p>
            </header>

            <form onSubmit={handleSubmit} className="space-y-5">
              {formError ? (
                <p
                  className="rounded-md border border-red-500/35 bg-red-500/10 px-3 py-2 text-center text-xs text-red-200"
                  role="alert"
                >
                  {formError}
                </p>
              ) : null}

              <div className="space-y-1">
                <label className="zx-label" htmlFor="onboarding-nickname">
                  Nickname
                </label>
                <div className={cn("rounded-md transition-shadow", nickFieldRing)}>
                  <input
                    id="onboarding-nickname"
                    name="nickname"
                    type="text"
                    autoComplete="username"
                    className="zx-input"
                    placeholder="Tu nombre en el club"
                    value={nickname}
                    onChange={onNicknameChange}
                    onBlur={onNicknameBlur}
                    maxLength={32}
                    data-testid="onboarding-register-nickname"
                  />
                </div>
                {nickHint.message ? (
                  <p className={cn("text-xs leading-snug", hintColor)} role="status">
                    {nickHint.message}
                  </p>
                ) : null}
              </div>

              <div className="space-y-1">
                <label className="zx-label" htmlFor="onboarding-password">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    id="onboarding-password"
                    name="password"
                    type={showPwd ? "text" : "password"}
                    autoComplete="new-password"
                    className="zx-input pr-10"
                    placeholder="Mínimo 8 caracteres"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFormError("");
                    }}
                    data-testid="onboarding-register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-[rgba(255,255,255,0.45)] hover:text-[#f5f5f5]"
                  >
                    {showPwd ? <EyeSlash size={18} weight="thin" /> : <Eye size={18} weight="thin" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <label className="zx-label" htmlFor="onboarding-password2">
                  Confirmar contraseña
                </label>
                <input
                  id="onboarding-password2"
                  name="password_confirmation"
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  className="zx-input"
                  placeholder="Repite la contraseña"
                  value={password2}
                  onChange={(e) => {
                    setPassword2(e.target.value);
                    setFormError("");
                  }}
                  data-testid="onboarding-register-password2"
                />
              </div>

              <button
                type="submit"
                className="zx-btn-primary mt-2"
                disabled={!canSubmit}
              >
                {registerMutation.isPending ? "Creando…" : "Crear acceso"}
              </button>
            </form>

            <button type="button" className="zx-btn-ghost mt-4" onClick={onBack}>
              Volver al último paso
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
